import React from 'react';
import { ReportData } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface CriticalityTableProps {
  summaryData: ReportData;
}

interface CriticalityDataItem {
  count: number;
  keys: {
    zero?: string; // Clave para count === 0 (opcional)
    one: string;   // Clave para count === 1
    other: string; // Clave para count > 1
  };
  severity: string;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500 text-white';
    case 'high':
      return 'bg-orange-500 text-white';
    case 'medium':
      return 'bg-yellow-500 text-white';
    case 'low':
      return 'bg-green-300 text-white';
    default:
      return 'bg-green-500 text-white';
  }
};

const getCriticalityData = (summaryData: ReportData): CriticalityDataItem[] => {
  const data: CriticalityDataItem[] = [
    {
      count: summaryData.num_trabajadores,
      keys: {
        zero: 'criticality.no_workers',
        one: 'criticality.workers',
        other: 'criticality.workers_plural',
      },
      severity:
        summaryData.num_trabajadores > 5
          ? 'high'
          : summaryData.num_trabajadores > 0
          ? 'medium'
          : 'correct',
    },
    {
      count: summaryData.num_ips,
      keys: {
        zero: 'criticality.no_ips',
        one: 'criticality.ips',
        other: 'criticality.ips_plural',
      },
      severity: summaryData.num_ips > 0 ? 'medium' : 'correct',
    },
    {
      count: summaryData.num_emails,
      keys: {
        zero: 'criticality.no_emails',
        one: 'criticality.emails',
        other: 'criticality.emails_plural',
      },
      severity:
        summaryData.num_emails > 5
          ? 'high'
          : summaryData.num_emails > 0
          ? 'medium'
          : 'correct',
    },
    {
      count: summaryData.num_filtraciones,
      keys: {
        zero: 'criticality.no_data_leaks',
        one: 'criticality.data_leak',
        other: 'criticality.data_leak_plural',
      },
      severity: summaryData.num_filtraciones > 0 ? 'critical' : 'correct',
    },
    {
      count: summaryData.num_subdomains,
      keys: {
        zero: 'criticality.no_subdomains',
        one: 'criticality.subdomains',
        other: 'criticality.subdomains_plural',
      },
      severity: summaryData.num_subdomains > 0 ? 'medium' : 'correct',
    },
    {
      count: summaryData.num_typosquatting,
      keys: {
        zero: 'criticality.no_typosquatting',
        one: 'criticality.typosquatting',
        other: 'criticality.typosquatting_plural',
      },
      severity: summaryData.num_typosquatting > 0 ? 'medium' : 'correct',
    },
    {
      count: summaryData.num_dominios_spoofeables,
      keys: {
        zero: 'criticality.no_spoofable_domains',
        one: 'criticality.spoofable_domain',
        other: 'criticality.spoofable_domain_plural',
      },
      severity: summaryData.num_dominios_spoofeables > 0 ? 'critical' : 'correct',
    },
    {
      count: summaryData.len_vulns,
      keys: {
        zero: 'criticality.no_vulnerabilities',
        one: 'criticality.vulnerabilities',
        other: 'criticality.vulnerabilities_plural',
      },
      severity:
        summaryData.len_vulns > 4
          ? 'high'
          : summaryData.len_vulns > 0
          ? 'medium'
          : 'correct',
    },
  ];

  if (summaryData.num_directories > 0) {
    const additionalData: CriticalityDataItem[] = [
      {
        count: summaryData.num_technologies,
        keys: {
          zero: 'criticality.no_technologies',
          one: 'criticality.technologies',
          other: 'criticality.technologies_plural',
        },
        severity: summaryData.num_technologies > 0 ? 'low' : 'correct',
      },
      {
        count: summaryData.num_directories,
        keys: {
          zero: 'criticality.no_directories',
          one: 'criticality.directories',
          other: 'criticality.directories_plural',
        },
        severity: summaryData.num_directories > 0 ? 'low' : 'correct',
      },
      {
        count: summaryData.len_dir_sensibles,
        keys: {
          zero: 'criticality.no_sensitive_directories',
          one: 'criticality.sensitive_directories',
          other: 'criticality.sensitive_directories_plural',
        },
        severity: summaryData.len_dir_sensibles > 0 ? 'high' : 'correct',
      },
      {
        count: summaryData.num_urls_without_waf,
        keys: {
          zero: 'criticality.no_urls_without_waf',
          one: 'criticality.urls_without_waf',
          other: 'criticality.urls_without_waf_plural',
        },
        severity: summaryData.num_urls_without_waf > 0 ? 'high' : 'correct',
      },
    ];
    data.push(...additionalData);
  }

  // Ordenar por severidad según un orden predefinido
  const severityLevels: { [key: string]: number } = {
    critical: 1,
    high: 2,
    medium: 3,
    low: 4,
    correct: 5,
  };

  data.sort((a, b) => severityLevels[a.severity] - severityLevels[b.severity]);
  return data;
};

const CriticalityTable: React.FC<CriticalityTableProps> = ({ summaryData }) => {
  const { t } = useTranslation();
  const data = getCriticalityData(summaryData);

  // Función para determinar cuál clave de traducción usar según el valor de count
  const renderDescription = (item: CriticalityDataItem) => {
    if (item.count === 0 && item.keys.zero) {
      return t(item.keys.zero);
    } else if (item.count === 1) {
      return t(item.keys.one, { count: item.count });
    } else {
      return t(item.keys.other, { count: item.count });
    }
  };

  return (
    <table className="min-w-full text-left bg-white border rounded-lg">
      <thead className="bg-gray-800 text-white">
        <tr>
          <th className="py-3 px-6 text-sm font-semibold tracking-wider text-center">
            Vulnerabilidad
          </th>
          <th className="py-3 px-6 text-sm font-semibold tracking-wider text-center">
            Severidad
          </th>
        </tr>
      </thead>
      <tbody>
        {data.length > 0 ? (
          data.map((item, index) => (
            <tr
              key={index}
              className="text-center bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
            >
              <td className="py-2 px-6 border text-left text-sm text-gray-700">
                {renderDescription(item)}
              </td>
              <td className="py-2 px-6 border text-center">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(
                    item.severity
                  )}`}
                >
                  {t(`severity.${item.severity}`)}
                </span>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={2} className="py-3 px-6 text-center">
              {t('criticality.noData')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default CriticalityTable;
